import { FC, useCallback, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TDefaultValues } from './specialPrices.popup.tsx';
import { format } from 'date-fns';
import { Button, Stack } from '@mui/material';
import { generalTabState } from '../../../../../generalTab.state.ts';
import { useBeforeClosePopup } from '../../../../../../../../../../shared/components/popup/hooks/useBeforeClosePopup.tsx';
import {
  FieldsRow,
  FormDatePicker,
  FormTextField,
} from '../../../../../../../../../../shared/components/form';
import { ITabState } from '../../../../../../../../states/tabState.model.ts';
import { IPopupWrapper } from '../../../../../../../../../../shared/components/popup/components/popup.component.tsx';
import { ConditionalFieldError } from '../../../../../../../../../../shared/components/conditionalFieldError/conditionalFieldError.component.tsx';
import { SamePeriodPopup } from './popups/samePeriod/samePeriod.popup.tsx';

export const SpecialPricesForm: FC<ISpecialPricesForm> = ({
  field,
  formMethods,
  stream,
  createSpecialPriceCategoriesInputs,
  allPricesData,
}) => {
  const { t } = useTranslation();
  const { formState, trigger } = formMethods || {};
  const { errors, isDirty, dirtyFields, isValid } = formState;
  const samePeriodPopup = useMemo(() => new SamePeriodPopup(), []);

  const hasExactDateMatch = (
    value: TDefaultValues,
    items: ISpecialPricesForm['allPricesData'],
  ): boolean => {
    return items.some(
      (item) =>
        item.fromDate === value.fromDate && item.toDate === value.toDate && item.id != value.id,
    );
  };

  const handleClick = useCallback(() => {
    if (isValid) {
      if (isDirty) {
        const values = formMethods.getValues() as TDefaultValues;
        if (values.fromDate) values.fromDate = format(values.fromDate, 'yyyy-MM-dd');
        if (values.toDate) values.toDate = format(values.toDate, 'yyyy-MM-dd');
        const isPricesForThisPeriod = hasExactDateMatch(values, allPricesData);
        if (isPricesForThisPeriod) {
          samePeriodPopup.stream.emit('open');
          return;
        }
        generalTabState.pub.updateSpecialPricesPos(values);
      }
      stream.emit('close');
    } else {
      trigger('fromDate');
      trigger('price1');
    }
  }, [isDirty, dirtyFields, isValid, allPricesData]);

  const pricesInputs = useMemo(
    () => createSpecialPriceCategoriesInputs(field, errors),
    [field, Boolean(errors['price1'])],
  );

  useBeforeClosePopup({
    stream,
    isDirty: !!Object.keys(dirtyFields).length,
  });

  return (
    <>
      <Stack spacing={2}>
        <FieldsRow>
          <FieldsRow width='100%' justifyContent='space-between'>
            <FormDatePicker
              width='318px'
              returnEmpty
              fieldProps={{ label: t('common.from_date'), autoFocus: 'fromDate' === field }}
              navigation={false}
              name='fromDate'
              error={Boolean(errors['fromDate'])}
              helperText={errors['fromDate'] ? t(String(errors['fromDate'].message)) : ''}
            />
            <FormDatePicker
              width='318px'
              returnEmpty
              fieldProps={{ label: t('common.to_date'), autoFocus: 'toDate' === field }}
              navigation={false}
              name='toDate'
              error={Boolean(errors['fromDate'])}
              controllerProps={{
                rules: {
                  deps: ['fromDate'],
                },
              }}
            />
          </FieldsRow>
        </FieldsRow>
        <FieldsRow>{pricesInputs}</FieldsRow>
      </Stack>
      <ConditionalFieldError
        errorMessage={t('article.at_least_one_price_category_should_be_not_empty')}
        fieldName='price1'
        sx={{ marginTop: '4px' }}
        {...{ errors }}
      />
      <FieldsRow>
        <FormTextField
          slotProps={{ htmlInput: { maxLength: 128 } }}
          width='100%'
          label={t('common.note')}
          name='description'
          autoFocus={'description' === field}
        />
      </FieldsRow>
      <Stack
        direction='row'
        spacing={2}
        sx={{
          marginTop: '34px',
          justifyContent: 'flex-end',
        }}
      >
        <Button disabled={!!Object.keys(errors)?.length} variant='contained' onClick={handleClick}>
          {t('common.save')}
        </Button>
      </Stack>
      <samePeriodPopup.Component />
    </>
  );
};

interface ISpecialPricesForm {
  createSpecialPriceCategoriesInputs: ITabState['createSpecialPriceCategoriesInputs'];
  field: string;
  allPricesData: ITabState['articleSpecialPrices'];
  stream: IPopupWrapper['stream'];
  formMethods: UseFormReturn<ITabState['articleSpecialPrices'][number]>;
}
